import { useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import { navigate } from '../router'
import type { Category, Client } from '../../shared/types'
import { parseDollarsToCents } from '../../shared/money'
import { formatInvoiceNumber } from '../../shared/invoiceNumber'
import { appToday } from '../datetime'
import { pushError, pushToast } from '../toast'

type Props =
  | { mode: 'create'; clientId?: undefined }
  | { mode: 'edit'; clientId: number }

const EMPTY: Partial<Client> = {
  simple_name: '',
  legal_name: '',
  address: '',
  contact_email: '',
  duration_mode: 'decimal',
  invoice_line_mode: 'category',
  invoice_format: '{CLIENT}-{YYYY}-{###}',
  invoice_next_number: 1,
  invoice_prefix: '',
  payment_terms_days: 30,
  accent_color: '#3b6ea5',
}

export default function ClientSettings(props: Props) {
  const isEdit = props.mode === 'edit'
  const [draft, setDraft] = useState<Partial<Client>>(EMPTY)
  const [rateStr, setRateStr] = useState('0')
  const [taxStr, setTaxStr] = useState('0')
  const [minHours, setMinHours] = useState('')
  const [maxHours, setMaxHours] = useState('')
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isEdit) return
    ;(async () => {
      try {
        const { client, categories } = await api.getClient(props.clientId!)
        setDraft(client)
        setRateStr((client.default_rate_cents / 100).toString())
        setTaxStr(((client.tax_rate_bp ?? 0) / 100).toString())
        setMinHours(client.target_weekly_min_minutes != null ? (client.target_weekly_min_minutes / 60).toString() : '')
        setMaxHours(client.target_weekly_max_minutes != null ? (client.target_weekly_max_minutes / 60).toString() : '')
        setCategories(categories)
      } catch (e) {
        pushError(`Couldn't load client: ${(e as Error).message}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [isEdit, isEdit ? props.clientId : 0])

  const set = <K extends keyof Client>(k: K, v: Client[K]) => setDraft((d) => ({ ...d, [k]: v }))

  const previewNumber = useMemo(() => {
    const today = appToday()
    try {
      return formatInvoiceNumber(draft.invoice_format || '', {
        prefix: draft.invoice_prefix ?? '',
        issuedDate: today,
        seq: draft.invoice_next_number ?? 1,
      })
    } catch {
      return '—'
    }
  }, [draft.invoice_format, draft.invoice_prefix, draft.invoice_next_number])

  function buildPatch(): Partial<Client> {
    const rateCents = parseDollarsToCents(rateStr) ?? 0
    const taxPct = parseFloat(taxStr)
    const taxBp = Number.isFinite(taxPct) ? Math.round(taxPct * 100) : 0
    const toMinutes = (s: string) => {
      const n = parseFloat(s)
      return s.trim() === '' || !Number.isFinite(n) ? null : Math.round(n * 60)
    }
    return {
      simple_name: draft.simple_name?.trim() || 'Untitled client',
      legal_name: draft.legal_name?.trim() || draft.simple_name?.trim() || 'Untitled client',
      address: draft.address ?? null,
      contact_email: draft.contact_email ?? null,
      default_rate_cents: rateCents,
      duration_mode: draft.duration_mode,
      invoice_line_mode: draft.invoice_line_mode,
      invoice_format: draft.invoice_format,
      invoice_next_number: draft.invoice_next_number,
      invoice_prefix: draft.invoice_prefix ?? null,
      payment_terms_days: draft.payment_terms_days,
      tax_rate_bp: taxBp,
      accent_color: draft.accent_color,
      target_weekly_min_minutes: toMinutes(minHours),
      target_weekly_max_minutes: toMinutes(maxHours),
    }
  }

  async function save() {
    setSaving(true)
    try {
      const patch = buildPatch()
      if (isEdit) {
        await api.updateClient(props.clientId!, patch)
        pushToast('Saved')
        navigate(`#/client/${props.clientId}`)
      } else {
        const created = await api.createClient(patch)
        pushToast('Client created — add categories next')
        navigate(`#/client/${created.id}/settings`)
      }
    } catch (e) {
      pushError(`Save failed: ${(e as Error).message}`)
    } finally {
      setSaving(false)
    }
  }

  // ── category ops (edit mode only) ──
  async function addCategory() {
    if (!isEdit) return
    try {
      const cat = await api.createCategory(props.clientId!, {
        name: 'New category',
        sort_order: categories.length,
      })
      setCategories((cs) => [...cs, cat])
    } catch (e) {
      pushError(`Couldn't add category: ${(e as Error).message}`)
    }
  }
  async function patchCategory(id: number, patch: Partial<Category>) {
    setCategories((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)))
    try {
      await api.updateCategory(id, patch)
    } catch (e) {
      pushError(`Couldn't update category: ${(e as Error).message}`)
    }
  }
  async function removeCategory(cat: Category) {
    try {
      await api.deleteCategory(cat.id)
      setCategories((cs) => cs.filter((c) => c.id !== cat.id))
    } catch (e) {
      // In use → deactivate instead (spec: deactivating must not break history).
      await patchCategory(cat.id, { active: 0 })
      pushToast('Category is in use by entries — deactivated instead of deleted')
    }
  }
  async function move(idx: number, dir: -1 | 1) {
    const j = idx + dir
    if (j < 0 || j >= categories.length) return
    const a = categories[idx]
    const b = categories[j]
    const reordered = [...categories]
    reordered[idx] = b
    reordered[j] = a
    setCategories(reordered)
    await Promise.all([
      api.updateCategory(a.id, { sort_order: j }),
      api.updateCategory(b.id, { sort_order: idx }),
    ]).catch((e) => pushError(`Reorder failed: ${(e as Error).message}`))
  }

  if (loading) return <div className="p-10 text-slate-400">Loading…</div>

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-10">
      <header className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {isEdit ? draft.simple_name || 'Client settings' : 'New client'}
        </h1>
        <button className="text-sm text-slate-500 hover:text-slate-800" onClick={() => navigate('#/')}>
          ← Clients
        </button>
      </header>

      <div className="flex flex-col gap-8">
        <Section title="Identity">
          <Field label="Simple name (shown in the app)">
            <Text value={draft.simple_name ?? ''} onChange={(v) => set('simple_name', v)} />
          </Field>
          <Field label="Legal name (shown on invoices)">
            <Text value={draft.legal_name ?? ''} onChange={(v) => set('legal_name', v)} />
          </Field>
          <Field label="Address">
            <textarea
              className={inputCls + ' min-h-20'}
              value={draft.address ?? ''}
              onChange={(e) => set('address', e.target.value)}
            />
          </Field>
          <Field label="Contact email">
            <Text value={draft.contact_email ?? ''} onChange={(v) => set('contact_email', v)} />
          </Field>
          <Field label="Accent color">
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="h-9 w-12 cursor-pointer rounded border border-slate-300"
                value={draft.accent_color ?? '#3b6ea5'}
                onChange={(e) => set('accent_color', e.target.value)}
              />
              <span className="tabular text-sm text-slate-500">{draft.accent_color}</span>
            </div>
          </Field>
        </Section>

        <Section title="Rates">
          <Field label="Default hourly rate">
            <div className="flex items-center gap-2">
              <span className="text-slate-400">$</span>
              <Text value={rateStr} onChange={setRateStr} className="w-32 tabular" inputMode="decimal" />
              <span className="text-sm text-slate-400">/ hour</span>
            </div>
          </Field>
          {isEdit ? (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Categories</span>
                <button className="text-sm text-[var(--accent)] hover:underline" onClick={addCategory}>
                  + Add category
                </button>
              </div>
              <div className="flex flex-col divide-y divide-slate-100 rounded-md border border-slate-200">
                {categories.length === 0 && (
                  <p className="px-3 py-3 text-sm text-slate-400">No categories yet.</p>
                )}
                {categories.map((cat, idx) => (
                  <CategoryRow
                    key={cat.id}
                    cat={cat}
                    defaultRateCents={parseDollarsToCents(rateStr) ?? 0}
                    onName={(name) => patchCategory(cat.id, { name })}
                    onRate={(rate_cents) => patchCategory(cat.id, { rate_cents })}
                    onActive={(active) => patchCategory(cat.id, { active })}
                    onDelete={() => removeCategory(cat)}
                    onUp={() => move(idx, -1)}
                    onDown={() => move(idx, 1)}
                  />
                ))}
              </div>
              <p className="mt-1.5 text-xs text-slate-400">
                Blank override inherits the default rate. Deactivating hides a category from new entries
                but keeps historical entries intact.
              </p>
            </div>
          ) : (
            <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-500">
              Categories can be added once the client is created.
            </p>
          )}
        </Section>

        <Section title="Time entry">
          <Radio
            value={draft.duration_mode ?? 'decimal'}
            onChange={(v) => set('duration_mode', v as Client['duration_mode'])}
            options={[
              { value: 'decimal', label: 'Decimal hours (e.g. 1.5)' },
              { value: 'startend', label: 'Start / end times' },
            ]}
          />
        </Section>

        <Section title="Weekly target">
          <p className="text-sm text-slate-500">
            Target hours per week. The dashboard flags this client under / in / over the band (Week and
            Month views). Leave blank for no target.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Min hours / week">
              <Text value={minHours} onChange={setMinHours} className="tabular" inputMode="decimal" />
            </Field>
            <Field label="Max hours / week">
              <Text value={maxHours} onChange={setMaxHours} className="tabular" inputMode="decimal" />
            </Field>
          </div>
        </Section>

        <Section title="Invoicing">
          <Field label="Number format">
            <Text
              value={draft.invoice_format ?? ''}
              onChange={(v) => set('invoice_format', v)}
              className="tabular"
            />
            <p className="mt-1 text-xs text-slate-400">
              Tokens: {'{CLIENT} {YYYY} {YY} {MM} {#} {##} {###} {####}'}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Next invoice will be{' '}
              <span className="tabular rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-800">
                {previewNumber}
              </span>
            </p>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Prefix ({CLIENT})">
              <Text value={draft.invoice_prefix ?? ''} onChange={(v) => set('invoice_prefix', v)} />
            </Field>
            <Field label="Next number">
              <Text
                value={String(draft.invoice_next_number ?? 1)}
                onChange={(v) => set('invoice_next_number', Number(v) || 1)}
                className="tabular"
                inputMode="numeric"
              />
            </Field>
            <Field label="Payment terms (days)">
              <Text
                value={String(draft.payment_terms_days ?? 30)}
                onChange={(v) => set('payment_terms_days', Number(v) || 0)}
                className="tabular"
                inputMode="numeric"
              />
            </Field>
            <Field label="Tax rate (%)">
              <Text value={taxStr} onChange={setTaxStr} className="tabular" inputMode="decimal" />
            </Field>
          </div>
          <Field label="Invoice line mode">
            <Radio
              value={draft.invoice_line_mode ?? 'category'}
              onChange={(v) => set('invoice_line_mode', v as Client['invoice_line_mode'])}
              options={[
                { value: 'category', label: 'Summarize by category' },
                { value: 'entry', label: 'Itemize every entry' },
              ]}
            />
          </Field>
        </Section>

        <div className="flex items-center justify-between border-t border-slate-200 pt-6">
          {isEdit ? (
            <button
              className="text-sm text-slate-500 hover:text-red-600"
              onClick={async () => {
                await api.updateClient(props.clientId!, { archived: draft.archived ? 0 : 1 })
                pushToast(draft.archived ? 'Unarchived' : 'Archived')
                navigate('#/')
              }}
            >
              {draft.archived ? 'Unarchive client' : 'Archive client'}
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            <button className="rounded-md px-4 py-2 text-slate-600 hover:bg-slate-100" onClick={() => navigate(isEdit ? `#/client/${props.clientId}` : '#/')}>
              Cancel
            </button>
            <button
              className="rounded-md bg-slate-900 px-4 py-2 font-medium text-white hover:bg-slate-700 disabled:opacity-50"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create client'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── small form primitives ───────────────────────────────────────────────────

const inputCls =
  'w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[var(--accent)]'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-4">{children}</div>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  )
}

function Text({
  value,
  onChange,
  className = '',
  inputMode,
}: {
  value: string
  onChange: (v: string) => void
  className?: string
  inputMode?: 'decimal' | 'numeric'
}) {
  return (
    <input
      className={inputCls + ' ' + className}
      value={value}
      inputMode={inputMode}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function Radio({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {options.map((o) => (
        <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input type="radio" checked={value === o.value} onChange={() => onChange(o.value)} />
          {o.label}
        </label>
      ))}
    </div>
  )
}

function CategoryRow({
  cat,
  defaultRateCents,
  onName,
  onRate,
  onActive,
  onDelete,
  onUp,
  onDown,
}: {
  cat: Category
  defaultRateCents: number
  onName: (v: string) => void
  onRate: (v: number | null) => void
  onActive: (v: number) => void
  onDelete: () => void
  onUp: () => void
  onDown: () => void
}) {
  const [name, setName] = useState(cat.name)
  const [rate, setRate] = useState(cat.rate_cents == null ? '' : (cat.rate_cents / 100).toString())
  useEffect(() => setName(cat.name), [cat.name])
  return (
    <div className={'flex items-center gap-2 px-3 py-2 ' + (cat.active ? '' : 'opacity-50')}>
      <div className="flex flex-col">
        <button className="text-xs text-slate-400 hover:text-slate-700" onClick={onUp} aria-label="Move up">
          ▲
        </button>
        <button className="text-xs text-slate-400 hover:text-slate-700" onClick={onDown} aria-label="Move down">
          ▼
        </button>
      </div>
      <input
        className="flex-1 rounded border border-transparent px-1.5 py-1 text-sm hover:border-slate-200 focus:border-[var(--accent)]"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => name !== cat.name && onName(name)}
      />
      <div className="flex items-center gap-1 text-sm text-slate-500">
        <span>$</span>
        <input
          className="w-20 rounded border border-transparent px-1.5 py-1 text-right text-sm tabular hover:border-slate-200 focus:border-[var(--accent)]"
          placeholder={(defaultRateCents / 100).toString()}
          value={rate}
          inputMode="decimal"
          onChange={(e) => setRate(e.target.value)}
          onBlur={() => {
            const trimmed = rate.trim()
            onRate(trimmed === '' ? null : Math.round(Number(trimmed) * 100))
          }}
        />
      </div>
      <label className="flex items-center gap-1 text-xs text-slate-500">
        <input type="checkbox" checked={!!cat.active} onChange={(e) => onActive(e.target.checked ? 1 : 0)} />
        active
      </label>
      <button className="text-slate-300 hover:text-red-500" onClick={onDelete} aria-label="Delete category">
        ✕
      </button>
    </div>
  )
}
